import {
  PrimaryButton,
  SwitchField,
  Text,
  TextField,
  useFormField,
} from '@weave/design-system';
import { listTagsQuery, getTagQuery } from 'apis/wetags/queriesV2';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CreateTagModal } from '../tag/CreateTagModal.component';
import { QueryNamespaceModal } from './components/QueryNamespaceModal.component';
import { setRecentlyAccessedNamespace } from './namespaceStorage';
import { Page } from 'components/shared';
import { getBreadcrumbs } from '../wetagsBreadcrumbs';
import { WetagsHeader } from '../components/WetagsHeader.component';
import {
  WetagsTable,
  cellStyle,
  rowStyle,
  headerCellStyle,
} from '../components/WetagsTable.component';
import { FormattedDate } from '../components/FormattedDate.component';

export function WetagsNamespaceRoot() {
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const location = useLocation();

  const { data = { tags: [] }, isLoading } = useQuery({
    ...listTagsQuery.getUseQueryOptions({ namespaceId }),
    refetchOnMount: true,
  });
  const hasTags = data.tags && data.tags.length > 0;
  const switchField = useFormField({ type: 'switch', value: false });
  const searchField = useFormField({ type: 'text' });

  // Save recently accessed namespace
  useEffect(() => {
    if (namespaceId) {
      setRecentlyAccessedNamespace(namespaceId);
    }
  }, [namespaceId]);

  const filteredTags = useMemo(
    () =>
      data.tags?.filter(
        (tag) =>
          (switchField.value || !tag.id?.startsWith('disable-')) &&
          (tag.id?.toLowerCase().includes(searchField.value.toLowerCase()) ||
            tag.description?.toLowerCase().includes(searchField.value.toLowerCase()))
      ) || [],
    [data.tags, searchField.value, switchField.value]
  );

  return (
    <Page
      title={namespaceId}
      breadcrumbs={getBreadcrumbs(location.pathname)}
      headerActions={
        <WetagsHeader>
          <PrimaryButton
            onClick={() => setIsQueryModalOpen(true)}
            style={{ width: 'auto', maxWidth: 'max-content' }}
          >
            Query
          </PrimaryButton>
          <PrimaryButton
            onClick={() => setIsCreateTagModalOpen(true)}
            style={{ width: 'auto', maxWidth: 'max-content' }}
          >
            Create Tag in {namespaceId}
          </PrimaryButton>
        </WetagsHeader>
      }
    >
      <h3>{hasTags ? 'Tags' : `No tags in ${namespaceId}`}</h3>
      <SwitchField
        {...switchField}
        label={
          <Text>
            Show <code>disable-</code> tags
          </Text>
        }
        name="show-disable-tags"
      />
      {hasTags && <TextField {...searchField} label="Search tags" name="search-tags" />}
      {!isLoading && hasTags && (
        <WetagsTable>
          <thead>
            <tr>
              <th style={headerCellStyle}>Name</th>
              <th style={headerCellStyle}>Description</th>
              <th style={headerCellStyle}>Percentage</th>
              <th style={headerCellStyle}>Created</th>
            </tr>
          </thead>
          <tbody>
            {filteredTags.map((tag) => (
              <tr key={tag.id} style={rowStyle}>
                <td style={cellStyle}>
                  <Link
                    to={`/wetags/namespace/${tag.namespaceId}/tag/${tag.id}`}
                    onMouseEnter={() =>
                      getTagQuery.prefetch({
                        namespaceId: tag.namespaceId,
                        tagId: tag.id,
                      })
                    }
                  >
                    {tag.id}
                  </Link>
                </td>
                <td style={cellStyle}>{tag.description || '—'}</td>
                <td style={cellStyle}>{tag.percentage ?? 0}%</td>
                <td style={cellStyle}>
                  <FormattedDate value={tag.createTime || 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </WetagsTable>
      )}
      <CreateTagModal
        isOpen={isCreateTagModalOpen}
        onClose={() => setIsCreateTagModalOpen(false)}
        namespaceId={namespaceId}
      />
      <QueryNamespaceModal
        isOpen={isQueryModalOpen}
        onClose={() => setIsQueryModalOpen(false)}
        namespaceId={namespaceId}
      />
    </Page>
  );
}
